import { IDateCommonSelectType, TComponentConfig } from '../types';
import { getDateDefaultValue } from '../utils';
import MultiDateSelect, { TMultiDateSelect } from '../widgets/MultiDateSelect';
import { GAME_CATEGORY } from '../constants/games';
import moment from 'moment';
import GameSelect, { TGameSelectType } from '../components/GameSelect';
import { gameSelectTop15TipsMap } from '@opm/components/BubbleCommon';



const COMPARE_GAME_MAX = 15;
/**
 * 多游戏对比专用游戏过滤器
 *
 * 与 ADGame 的差异：
 * 1. 使用 CompareGameSelect 组件（最多 15 个；超过截断）
 * 2. defaultValue 尊重外部传入的 game_category（不强制覆盖为 ALL）
 * 3. 默认游戏列表 = 当前分类下的前 15 个 game_id（接口契约 game_ids ≤ 15）
 * 4. backValue 直接返回选中的 ID 列表（不做"全选→[]"压缩 → 后端按 ID 查询）
 *
 * 用于：CompetitorAdComparison 多游戏投放对比页
 */
export const ADCompareGame: TComponentConfig<TGameSelectType> = {
  Component: GameSelect,
  defaultValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props;
    const { category, games } = valueKeys;
    // 1) 尊重外部传入的 category；缺省才回落到 ALL
    const incomingCategory = filterValue?.[category];
    const realCategory = incomingCategory || GAME_CATEGORY.ALL;

    // 2) 已有 games 选择则保留并截断到 15；否则按 category 取前 15 个
    const existing = filterValue?.[games];
    let nextGames: string[];
    if (Array.isArray(existing) && existing.length > 0) {
      nextGames = existing.slice(0, COMPARE_GAME_MAX);
    } else {
      const filtered =
        realCategory === GAME_CATEGORY.ALL
          ? options
          : (options || []).filter((g) => g.genre === realCategory);
      nextGames = (filtered || [])
        .slice(0, COMPARE_GAME_MAX)
        .map((g) => g.game_id);
    }

    return {
      [category]: realCategory,
      [games]: nextGames,
    };
  },
  backValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys } = props || ({} as any);
    const { category, games } = valueKeys;
    const value: string[] = filterValue?.[games] || [];
    return {
      [category]: filterValue?.[category],
      // 不做"全选→[]"压缩：本接口需要明确的 ID 列表；同时强制截断到 15
      [games]: value.slice(0, COMPARE_GAME_MAX),
    };
  },
  requestKey: 'materials_config_games',
  props: {
    title: 'Games',
    titleCn: '游戏',
    valueKeys: {
      category: 'game_category',
      games: 'game_ids',
    },
    // CompareGameSelect 不渲染 "All Games" 单一聚合项（要 ID 列表）
    allOption: false,
    options: [],
    listWidth: 350,
    listHeight: 300,
    searchable: true,
    matchButtonWidth: false,
    showTips: true,
    hasClearAll: true,
    hasSelectAll: true,
    maxSelectCount: COMPARE_GAME_MAX,
    maxSelectTipsType: "bubble",
    maxSelectTips: gameSelectTop15TipsMap,
    maxSelectTooltip: `Up to ${COMPARE_GAME_MAX} games can be selected`,
    maxSelectTooltipCn: `最多选择 ${COMPARE_GAME_MAX} 个游戏`,
  },
};

export const ADCompareMultiDateRange: TComponentConfig<TMultiDateSelect> = {
  Component: MultiDateSelect,
  props: {
    valueKeys: {
      start_time: 'start_time',
      end_time: 'end_time',
      date_type: 'date_type',
    },
    optionsMap: {
      date: { value: 'date', text: 'Day', text_cn: '日' },
      week: { value: 'week', text: 'Week', text_cn: '周' },
      month: { value: 'month', text: 'Month', text_cn: '月' },
    },
    typeList: ['date', 'week', 'month'],
    defaultValue: {
      date: [moment().subtract(1, 'day'), moment().subtract(1, 'day')],
      week: [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
      month: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
    },
    className: 'dbom-ad-multi-date-range',
  },
  defaultValue: (filterValue: any, props: IDateCommonSelectType) => {
    return getDateDefaultValue(filterValue, props);
  },
};
